import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

let stompClient = null;
let isConnecting = false;
let retryCount = 0;
const MAX_RETRIES = 3;
let connectionPromise = null;
let connectionResolve = null;

const getToken = () => {
  return localStorage.getItem('token');
};

// Wait for WebSocket connection to be established
const waitForConnection = (timeout = 5000) => {
  return new Promise((resolve, reject) => {
    // If already connected, resolve immediately
    if (stompClient && stompClient.connected) {
      resolve(stompClient);
      return;
    }

    // Poll for connection status
    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (stompClient && stompClient.connected) {
        clearInterval(checkInterval);
        resolve(stompClient);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error('Connection timeout'));
      }
    }, 100);
  });
};

export const initializeGameWebSocket = () => {
  if (stompClient && stompClient.connected) {
    return stompClient;
  }

  if (isConnecting) {
    return stompClient;
  }

  const token = getToken();
  if (!token) {
    // Don't log error - this is expected for anonymous users
    // Only log if we're actually trying to connect (not just checking)
    return null;
  }

  isConnecting = true;

  // Get WebSocket URL from environment variable (points to API Gateway)
  // SockJS automatically converts:
  //   http:// → ws:// (unencrypted WebSocket)
  //   https:// → wss:// (encrypted WebSocket)
  const getWebSocketUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    // Remove trailing slash if present
    const baseUrl = apiUrl.replace(/\/$/, '');
    // Game WebSocket endpoint through API Gateway (different from chat WebSocket)
    // SockJS will automatically use ws:// or wss:// based on http:// or https://
    return `${baseUrl}/ws/game`;
  };

  const socket = new SockJS(getWebSocketUrl());
  stompClient = new Client({
    webSocketFactory: () => socket,
    reconnectDelay: 5000,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
    connectHeaders: {
      Authorization: `Bearer ${token}`
    },
    onConnect: () => {
      console.log('Game WebSocket Connected');
      isConnecting = false;
      // Resolve any pending connection promises
      if (connectionResolve) {
        connectionResolve(stompClient);
        connectionResolve = null;
        connectionPromise = null;
      }
    },
    onStompError: (frame) => {
      console.error('Game STOMP error:', frame);
      isConnecting = false;
      // Reject any pending connection promises
      if (connectionResolve) {
        connectionResolve = null;
        connectionPromise = null;
      }
    },
    onWebSocketClose: () => {
      console.log('Game WebSocket closed');
      isConnecting = false;
      connectionResolve = null;
      connectionPromise = null;
    },
    onDisconnect: () => {
      console.log('Game WebSocket disconnected');
      isConnecting = false;
      connectionResolve = null;
      connectionPromise = null;
    },
  });

  stompClient.activate();
  return stompClient;
};

export const subscribeToGame = async (gameId, callback) => {
  // Check if we have a token first - if not, don't try to connect
  const token = getToken();
  if (!token) {
    // No token means anonymous user - WebSocket not needed for computer games
    return null;
  }

  // If already connected, subscribe immediately
  if (stompClient && stompClient.connected) {
    retryCount = 0; // Reset retry count on successful connection
    const subscription = stompClient.subscribe(`/topic/game/${gameId}`, (message) => {
      try {
        const game = JSON.parse(message.body);
        callback(game);
      } catch (e) {
        console.error('Error parsing game message:', e);
      }
    });
    console.log(`✅ Subscribed to game ${gameId}`);
    return subscription;
  }

  // If not connected, initialize and wait for connection
  if (!stompClient || !stompClient.connected) {
    const client = initializeGameWebSocket();
    if (!client) {
      // Failed to initialize (no token or other error)
      return null;
    }

    // Wait for connection with retry logic
    try {
      await waitForConnection(5000);
      // Connection established, now subscribe
      retryCount = 0; // Reset retry count on successful connection
      const subscription = stompClient.subscribe(`/topic/game/${gameId}`, (message) => {
        try {
          const game = JSON.parse(message.body);
          callback(game);
        } catch (e) {
          console.error('Error parsing game message:', e);
        }
      });
      console.log(`✅ Subscribed to game ${gameId}`);
      return subscription;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      // Retry if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.log(`Retrying subscription (attempt ${retryCount}/${MAX_RETRIES})...`);
        return new Promise((resolve) => {
          setTimeout(async () => {
            const result = await subscribeToGame(gameId, callback);
            resolve(result);
          }, 1000);
        });
      } else {
        retryCount = 0; // Reset for next attempt
        console.error('Max retries exceeded for WebSocket subscription');
        return null;
      }
    }
  }

  return null;
};

export const sendMove = (gameId, username, row, col) => {
  if (!stompClient || !stompClient.connected) {
    console.error('WebSocket not connected');
    return;
  }

  stompClient.publish({
    destination: '/app/game.move',
    body: JSON.stringify({ gameId, username, row, col })
  });
};

export const isConnected = () => {
  return stompClient && stompClient.connected;
};

export const disconnectGameWebSocket = () => {
  if (stompClient) {
    stompClient.deactivate();
    stompClient = null;
    isConnecting = false;
    retryCount = 0; // Reset retry count on disconnect
    connectionResolve = null;
    connectionPromise = null;
  }
};

